'use client';

// next/dist/compiled/web-vitals ist ein internes Next-Modul ohne .d.ts.
// Typen werden hier lokal deklariert; Runtime-Werte kommen aus dem Modul.
// @ts-expect-error -- internal next module, no published types
import { onLCP, onFID, onINP, onCLS, onFCP, onTTFB } from 'next/dist/compiled/web-vitals';

export type WebVitalName = 'LCP' | 'FID' | 'INP' | 'CLS' | 'FCP' | 'TTFB';

export type WebVitalRating = 'good' | 'needs-improvement' | 'poor';

export interface Metric {
  id: string;
  name: WebVitalName;
  value: number;
  rating: WebVitalRating;
  delta: number;
  entries: PerformanceEntry[];
  navigationType?: string;
}

export interface WebVitalMetric {
  name: WebVitalName;
  value: number;
  rating: WebVitalRating;
  delta: number;
  id: string;
  navigationType: string;
  timestamp: number;
}

export type WebVitalSink = (metric: WebVitalMetric) => void;

const STORAGE_KEY = 'kontora_web_vitals_v1';
const MAX_ENTRIES = 100;

export function getStoredVitals(): WebVitalMetric[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed as WebVitalMetric[];
  } catch {
    return [];
  }
}

export function clearStoredVitals(): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(STORAGE_KEY);
}

export function reportWebVital(metric: Metric): void {
  if (typeof window === 'undefined') return;
  const report: WebVitalMetric = {
    name: metric.name,
    value: metric.value,
    rating: metric.rating,
    delta: metric.delta,
    id: metric.id,
    navigationType: metric.navigationType ?? 'unknown',
    timestamp: Date.now(),
  };
  try {
    const vitals = getStoredVitals();
    vitals.push(report);
    if (vitals.length > MAX_ENTRIES) vitals.shift();
    localStorage.setItem(STORAGE_KEY, JSON.stringify(vitals));
  } catch (err) {
    console.warn('[web-vitals] Persistierung fehlgeschlagen:', err);
  }
}

let initialized = false;

export function initWebVitals(): void {
  if (initialized) return;
  if (typeof window === 'undefined') return;
  initialized = true;
  onLCP(reportWebVital);
  onFID(reportWebVital);
  onINP(reportWebVital);
  onCLS(reportWebVital);
  onFCP(reportWebVital);
  onTTFB(reportWebVital);
}

export interface WebVitalSummary {
  metric: WebVitalName;
  count: number;
  average: number;
  p75: number;
  p95: number;
  good: number;
  needsImprovement: number;
  poor: number;
}

function percentile(sortedAsc: number[], p: number): number {
  if (sortedAsc.length === 0) return 0;
  const idx = Math.min(sortedAsc.length - 1, Math.floor(sortedAsc.length * p));
  return sortedAsc[idx] ?? 0;
}

export function summarizeVitals(vitals: WebVitalMetric[]): WebVitalSummary[] {
  const byMetric = new Map<WebVitalName, WebVitalMetric[]>();
  for (const v of vitals) {
    const list = byMetric.get(v.name) ?? [];
    list.push(v);
    byMetric.set(v.name, list);
  }

  const summaries: WebVitalSummary[] = [];
  for (const [name, entries] of byMetric.entries()) {
    const values = entries.map((e) => e.value).sort((a, b) => a - b);
    const sum = values.reduce((a, b) => a + b, 0);
    const average = values.length > 0 ? sum / values.length : 0;
    const p75 = percentile(values, 0.75);
    const p95 = percentile(values, 0.95);

    summaries.push({
      metric: name,
      count: entries.length,
      average,
      p75,
      p95,
      good: entries.filter((e) => e.rating === 'good').length,
      needsImprovement: entries.filter((e) => e.rating === 'needs-improvement').length,
      poor: entries.filter((e) => e.rating === 'poor').length,
    });
  }
  return summaries;
}
